
import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Card } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { ChevronLeft, Star, MessageSquare, ThumbsUp, Award, Calendar } from "lucide-react";
import { cn } from "@/lib/utils";

// Sample submissions data
const submissionsData = {
  "1": {
    id: "1",
    author: "WritingWizard",
    category: "Copywriting",
    date: "May 15, 2023",
    title: "Revolutionary AI-Powered Water Bottle",
    prompt: "Write a compelling product description for a revolutionary new AI-powered smart water bottle that tracks hydration and reminds users to drink water.",
    content: `<h1>Introducing HydroGenius: Your Personal Hydration Coach</h1>
      
      <p>Never miss a sip again with HydroGenius – the world's first AI-powered smart water bottle that thinks about your hydration, so you don't have to.</p>
      
      <p>HydroGenius transforms your daily water intake from an afterthought into an effortless habit. Using advanced sensors and machine learning, this sleek, minimalist bottle learns your unique hydration patterns and needs, adapting its reminders based on your activity level, local climate, and personal health goals.</p>
      
      <p>Unlike ordinary bottles, HydroGenius features a subtle glow that pulses when it's time to drink – no intrusive notifications, just an elegant reminder that blends seamlessly into your day. The responsive LED base changes color throughout the day, reflecting your hydration status at a glance.</p>
      
      <h2>Smart Features That Flow With Your Life:</h2>
      <ul>
        <li>Personalized AI hydration coach that learns your patterns</li>
        <li>Adaptive reminders based on your activity, weather, and goals</li>
        <li>Temperature maintenance keeps your drink cold for 24 hours or hot for 12</li>
        <li>Minimalist app integration with health insights, not overwhelming data</li>
        <li>One-month battery life with wireless charging</li>
      </ul>
      
      <p>With HydroGenius, optimal hydration becomes an intuitive part of your wellness journey rather than another item on your to-do list. It's not just a water bottle – it's your personal hydration partner, elegantly designed to enhance your health one perfectly timed sip at a time.</p>
      
      <p>Available in Glacier White, Midnight Blue, and Sunset Orange. Your journey to effortless hydration starts here.</p>`,
    scores: [
      { category: "Relevance", score: 96 },
      { category: "Creativity", score: 98 },
      { category: "Structure", score: 99 },
      { category: "Language", score: 97 },
      { category: "Impact", score: 100 },
    ],
    totalScore: 98,
    likes: 42,
    comments: 7,
  },
  // Add more submission data as needed
};

export default function SubmissionPage() {
  const { id } = useParams<{ id: string }>();
  const [submission, setSubmission] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    // Simulate API fetch
    setTimeout(() => {
      if (id && submissionsData[id as keyof typeof submissionsData]) {
        setSubmission(submissionsData[id as keyof typeof submissionsData]);
      }
      setLoading(false);
    }, 500);
  }, [id]);
  
  if (loading) {
    return (
      <div className="min-h-screen">
        <Navbar />
        <main className="container max-w-4xl pt-28 px-4">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-card/50 rounded w-1/3"></div>
            <div className="h-40 bg-card/50 rounded"></div>
            <div className="h-96 bg-card/50 rounded"></div>
          </div>
        </main>
      </div>
    );
  }
  
  if (!submission) {
    return (
      <div className="min-h-screen">
        <Navbar />
        <main className="container max-w-4xl pt-28 px-4 text-center">
          <h1 className="text-2xl font-bold mb-4">Submission Not Found</h1>
          <p className="text-white/70 mb-6">The submission you're looking for doesn't exist or has been removed.</p>
          <Button asChild>
            <Link to="/leaderboard">Return to Leaderboard</Link>
          </Button>
        </main>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen pb-16">
      <Navbar />
      
      <main className="container max-w-4xl pt-28 px-4">
        <Button 
          asChild 
          variant="ghost" 
          className="mb-6 pl-0 hover:bg-transparent hover:text-primary"
        >
          <Link to="/leaderboard" className="flex items-center">
            <ChevronLeft className="mr-1 h-5 w-5" />
            Back to Leaderboard
          </Link>
        </Button>
        
        <div className="glass-card p-6 rounded-xl mb-8">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
            <div className="flex items-center">
              <Avatar className="h-12 w-12 mr-4 border-2 border-primary">
                <AvatarFallback className="bg-primary/20 text-primary">
                  {submission.author.substring(0, 2)}
                </AvatarFallback>
              </Avatar>
              
              <div>
                <h1 className="text-xl font-bold font-heading">{submission.author}</h1>
                <div className="flex items-center text-white/60 text-sm">
                  <Calendar className="h-3 w-3 mr-1" />
                  {submission.date}
                  <span className="mx-2">•</span>
                  {submission.category}
                </div>
              </div>
            </div>
            
            <div className="flex items-center space-x-3">
              <div className="flex items-center">
                <ThumbsUp className="h-4 w-4 text-white/60 mr-1" />
                <span className="text-white/60 text-sm">{submission.likes}</span>
              </div>
              <div className="flex items-center">
                <MessageSquare className="h-4 w-4 text-white/60 mr-1" />
                <span className="text-white/60 text-sm">{submission.comments}</span>
              </div>
              <div className="flex items-center bg-primary/20 text-primary font-medium px-3 py-1 rounded-full">
                <Award className="h-4 w-4 mr-1" />
                {submission.totalScore}
              </div>
            </div>
          </div>
          
          <div className="mb-6">
            <div className="bg-card/50 p-4 rounded-lg mb-4 border border-white/10">
              <h3 className="text-sm font-medium text-white/70 mb-2">Prompt:</h3>
              <p className="text-white/90">{submission.prompt}</p>
            </div>
            <h2 className="text-2xl font-bold font-heading mb-4">{submission.title}</h2>
          </div>
          
          <div 
            className="prose prose-invert max-w-none prose-headings:font-heading prose-headings:text-white prose-p:text-white/90 prose-li:text-white/90"
            dangerouslySetInnerHTML={{ __html: submission.content }}
          />
        </div>
        
        <Card className="glass-card p-6 rounded-xl">
          <h2 className="text-xl font-bold font-heading mb-6 flex items-center">
            <Star className="text-primary mr-2 h-5 w-5" />
            Scoring Breakdown
          </h2>
          
          <div className="space-y-4">
            {submission.scores.map((score: any, index: number) => (
              <div key={index}>
                <div className="flex justify-between mb-1">
                  <span className="text-white/80">{score.category}</span>
                  <span className="font-medium">{score.score}/100</span>
                </div>
                <Progress 
                  value={score.score} 
                  className="h-2 bg-muted" 
                  indicatorClassName={cn(
                    score.score >= 95 ? "bg-primary" : "bg-secondary"
                  )}
                />
              </div>
            ))}
            
            <div className="pt-4 mt-4 border-t border-white/10">
              <div className="flex justify-between mb-1">
                <span className="font-bold">Overall Score</span>
                <span className="font-bold text-primary">{submission.totalScore}/100</span>
              </div>
              <Progress 
                value={submission.totalScore} 
                className="h-3 bg-muted" 
                indicatorClassName="bg-gradient-to-r from-primary to-secondary"
              />
            </div>
          </div>
        </Card>
      </main>
    </div>
  );
}
